import { Grid, TextField, Typography, MenuItem, Box } from "@mui/material";
import { useCallback, useContext, useEffect } from "react";
import { DbUser, ClassExtended, CourseStructureCtx } from "../../../../interfaces";
import { UserManagementModal } from "../UserManagementModal";
import axios from "../../../../services/axios";
import { AlertState } from "../../../../types";
import { TechnicalIssuesMessage } from "../../utils/Toast";
import { AxiosError } from "axios";
import { SchoolSelect } from "../utils/SchoolSelect";
import { TeacherSelect } from "../utils/TeacherSelect";
import { Role } from "../../../../enums";
import {
  ClassEditState,
  getError,
  initClassEditState,
  useClassEditState,
} from "../state/useClassEditState";
import { makeStyles } from "@mui/styles";
import { v2theme } from "../../../../styles/Theme";
import { CourseStructureContext } from "../../../../context/CourseStructureContext";
import { formatDate, generateGtmEvent, HandleDataLayer } from "../../../../Helpers";
import { CustomCheckBox } from "../../utils/CustomCheckBox";
import { ClassDetailEditableItem } from "../../../v3/insights/classSettings/ClassDetails";

export type EditClassProps = {
  open: boolean;
  class?: ClassExtended;
  onClose: () => void;
  onSubmit: (alert: AlertState) => void;
  currentUser: DbUser;
};

export type ClassRequest = Pick<
  ClassExtended,
  "end_date" | "period_block" | "course_id" | "teacher_id" | "start_date"
>;

const useStyle = makeStyles((theme) => ({
  inputText: {
    "& .MuiOutlinedInput-root": {
      "&:hover fieldset": {
        borderColor: v2theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: v2theme.palette.primary.main,
      },
    },
  },
}));

export function EditClass(props: EditClassProps) {
  const classes = useStyle();
  const [state, dispatch] = useClassEditState(props);
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const displaySubRole = props.currentUser?.role === Role.super;

  // limit selection to available courses based on role
  // but include the currently selected course in case the class is set to one that's unavailable
  const selectableCourses =
    props.currentUser.role === Role.super
      ? courseStructureCtx.allCourses
      : courseStructureCtx.allCourses.filter(
          (item) =>
            !item.metadata?.management_page?.super_admins_only ||
            state.form.courseId === item.id
        );

  const updateForm = useCallback(
    (update: Partial<ClassEditState["form"]>, extendedClass?: ClassExtended) =>
      dispatch({ type: "updateForm", update, extendedClass }),
    [dispatch]
  );

  const formModified = () => {
    const schoolId = props.class?.school_id.toString() ?? null;

    // console.log(
    //   `${String(state.form.schoolId ?? "")} === ${String(schoolId ?? "")}`,
    //   String(state.form.schoolId ?? "") === String(schoolId ?? "")
    // );

    return !(
      String(state.form.schoolId ?? "") === String(schoolId ?? "") &&
      String(state.form.teacherId ?? "") === String(props.class?.teacher_id ?? "") &&
      state.form.courseId === props.class?.course_id &&
      state.form.period === props.class?.period_block &&
      state.form.startDate === props.class?.start_date &&
      state.form.endDate === props.class?.end_date &&
      state.form.isTester === props.class?.is_tester
    );
  };

  const handleClose = () => {
    // generateGtmEvent({
    //   event: "modal",
    //   action: "close",
    //   name: "onclick",
    //   type: "click",
    //   region: "main content",
    //   section: "edit class",
    //   text: "close cross",
    // });
    props.onClose();
  };

  const submit = async () => {
    HandleDataLayer(
      "form",
      "click",
      "click",
      "main content",
      "edit class",
      "save",
      "onclick"
    );
    const { form } = state;
    const course = courseStructureCtx.allCourses.find(
      (item) => item.id === form.courseId
    );
    const className = `${course?.name} ${form.period} ${
      form.startDate ? new Date(form.startDate).toLocaleDateString() : ""
    }`;

    try {
      await axios.post<ClassRequest>("/class", {
        id: props.class?.id,
        teacher_id: form.teacherId,
        course_id: form.courseId,
        course_name: course?.name,
        period_block: form.period,
        start_date: form.startDate!,
        end_date: form.endDate!,
        school_id: Number(form.schoolId),
        is_tester: form.isTester,
      });
      props.onSubmit({
        severity: "success",
        message: `Edited ${className}`,
      });
      const selectedSchool = state.data.schools.value.find(
        (item) => item.id === Number(form.schoolId)
      );
      const selectedTeacher = state.data.teachers.value.find(
        (item) => item.id === form.teacherId
      );
      generateGtmEvent(
        {
          event: "form",
          action: "editing a class",
          name: "onsubmit",
          type: "submit",
          region: "main content",
          section: "edit class",
          text: "class edited",
        },
        {
          school: `${selectedSchool ? selectedSchool.name : ""}`,
          teacher: `${selectedTeacher ? selectedTeacher.full_name : ""}`,
          course: course?.name ?? "",
          "period/block": form.period,
          start_date: form.startDate ? formatDate(new Date(form.startDate)) : "",
          end_date: form.endDate ? formatDate(new Date(form.endDate)) : "",
          user_interface_testing: form.isTester ? "yes" : "no",
        }
      );
      dispatch({
        type: "replaceState",
        state: initClassEditState(props.currentUser),
      });
    } catch (e: any) {
      console.error(e);
      if (e instanceof AxiosError) {
        if (e?.response?.status === 401) {
          props.onSubmit({
            severity: "error",
            message:
              "The class you are trying to edit belongs to another school. Please try another class.",
          });
        }
        if (e?.response?.status === 404) {
          props.onSubmit({
            severity: "error",
            message:
              "The teacher being linked to the class does not or no longer exists. Please try linking another teacher.",
          });
        }
      }
      if (
        e instanceof AxiosError &&
        typeof e.response?.data === "string" &&
        e.response?.data.includes("DuplicateClassNameException")
      ) {
        props.onSubmit({
          severity: "error",
          message: `${className} already exists. Please use another name.`,
        });
      } else {
        props.onSubmit({
          severity: "error",
          message: TechnicalIssuesMessage(),
        });
      }
      throw e;
    }
  };

  useEffect(() => {
    if (props.open) {
      updateForm(
        {
          teacherId: props.class?.teacher_id ?? "",
          schoolId: props.class?.school_id ? props.class?.school_id.toString() : "",
          courseId: props.class?.course_id,
          period: props.class?.period_block ? props.class?.period_block : "",
          startDate: props.class?.start_date,
          endDate: props.class?.end_date,
        },
        props.class
      );
    } else {
      updateForm(
        {
          teacherId: undefined,
          schoolId: undefined,
          courseId: undefined,
          period: undefined,
          startDate: undefined,
          endDate: undefined,
        },
        undefined
      );
    }
  }, [props.class, props.open, updateForm]);

  return (
    <UserManagementModal
      title="Edit Class"
      open={props.open}
      onClose={handleClose}
      onSave={submit}
      disableSubmit={!formModified()}
      errorText={getError(state)}>
      <Typography color="textSecondary" sx={{ marginBottom: "2rem" }}>
        For the class name please select a unique combination of subject, period/block,
        and start date. A duplicate class name cannot be created.
        <br></br>
        The school and teacher must exist before creating a class.
      </Typography>
      <Grid container spacing={6}>
        <Grid item xs={6}>
          <SchoolSelect
            schoolId={state.form.schoolId ? state.form.schoolId.toString() : ""}
            onChange={(schoolId) => {
              generateGtmEvent({
                event: "select",
                action: "click",
                name: "onclick",
                type: "select a school",
                region: "main content",
                section: "edit class ^ school",
                text:
                  state.data.schools.value.find((item) => `${item.id}` === `${schoolId}`)
                    ?.name ?? `${schoolId}`,
              });
              updateForm({ schoolId });
            }}
            options={state.data.schools.value}
            required={true}
            disabled={props.currentUser.role !== Role.super}
          />
        </Grid>
        <Grid item xs={6}>
          <TeacherSelect
            teacherId={state.form.teacherId}
            onChange={(teacherId) => {
              generateGtmEvent({
                event: "select",
                action: "click",
                name: "onclick",
                type: "select a teacher",
                region: "main content",
                section: "edit class ^ teacher",
                text:
                  state.data.teachers.value.find(
                    (item) => `${item.id}` === `${teacherId}`
                  )?.full_name ?? `${teacherId}`,
              });
              updateForm({ teacherId });
            }}
            disabled={props.currentUser.role === Role.teacher || !state.form.schoolId}
            required={true}
            options={state.data.teachers.value}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            className={classes.inputText}
            required
            select
            label="Course"
            value={state.form.courseId}
            type="number"
            onChange={(e) => {
              generateGtmEvent({
                event: "select",
                action: "click",
                name: "onclick",
                type: "select a course",
                region: "main content",
                section: "edit class ^ course",
                text:
                  selectableCourses.find((item) => `${item.id}` === `${e.target.value}`)
                    ?.name ?? `${e.target.value}`,
              });
              updateForm({ courseId: Number(e.target.value) });
            }}
            fullWidth>
            {selectableCourses.map((item, index) => {
              return (
                <MenuItem key={`subject-${index}-${item.name}`} value={item.id}>
                  {item.name}
                </MenuItem>
              );
            })}
            {/* <MenuItem value="6th Grade Math">6th Grade Math</MenuItem>
            <MenuItem value="7th Grade Math">7th Grade Math</MenuItem>
            <MenuItem value="8th Grade Math">8th Grade Math</MenuItem>
            <MenuItem value="Pre-Algebra">Pre-Algebra</MenuItem>
            <MenuItem value="Algebra I">Algebra I</MenuItem>
            <MenuItem value="Algebra II">Algebra II</MenuItem>
            <MenuItem value="Geometry">Geometry</MenuItem> */}
          </TextField>
        </Grid>
        <Grid item xs={6}>
          <TextField
            className={classes.inputText}
            required
            fullWidth
            label="Period/Block"
            value={state.form.period}
            onChange={(period) => {
              generateGtmEvent({
                event: "form",
                action: "click",
                name: "onclick",
                type: "blur",
                region: "main content",
                section: "edit class ^ period/block",
                text: `${period.target.value}`,
              });
              updateForm({ period: period.target.value });
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <ClassDetailEditableItem
            variant={"datepicker"}
            value={state.form.startDate && formatDate(new Date(state.form.startDate))}
            label={"Start Date"}
            placeholder={"Start date"}
            isVisible={true}
            onChange={(e) => {
              updateForm({ startDate: new Date(e) });
            }}
            disabled={false}
          />
        </Grid>
        <Grid item xs={6}>
          <ClassDetailEditableItem
            variant={"datepicker"}
            value={state.form.endDate && formatDate(new Date(state.form.endDate))}
            label={"End Date"}
            placeholder={"End date"}
            isVisible={true}
            onChange={(e) => {
              updateForm({ endDate: new Date(e) });
            }}
            disabled={false}
          />
        </Grid>
      </Grid>
      {displaySubRole && (
        <Box sx={{ marginTop: "24px" }}>
          <Typography
            variant="h6"
            sx={{
              color: "#000000",
              fontWeight: "bold",
              fontSize: "24px",
            }}>
            User interface testing
          </Typography>
          <CustomCheckBox
            label={
              "Please check this box if the class and all students in it are testers."
            }
            checked={state.form.isTester}
            onChange={(e) => updateForm({ isTester: e.target.checked }, props.class)}
            emitDataLayer={(checked) => {
              HandleDataLayer(
                "select",
                checked ? "click" : "unclick",
                "checkbox",
                "main content",
                "user interface testing",
                "please check this box if the class and all students in it are testers.",
                "onclick",
                "edit class"
              );
            }}
          />
        </Box>
      )}
    </UserManagementModal>
  );
}
